import { ReactNode, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, FilterBar, Inline, Segmented, Stack, t } from '@kanbios/ui';
import type { DataTableColumn } from '@kanbios/ui';
import { FULL_HEIGHT } from '../lib/tables';

/** D120: what every list of the portal answers (ListMeta and its rows). */
export interface ListResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  facets: Record<string, Record<string, number>>;
  views: Record<string, number>;
}

export interface ListFacet {
  key: string;
  label: string;
  /** The words of a value (a state's label, an environment's name); the value itself otherwise. */
  valueLabel?: (value: string) => string;
}

/**
 * D120: one table for every list of a page's tab. The server searches, filters, sorts and pages
 * (`?q&page&pageSize&sort&filter[facet]`); the DSM DataTable shows the page in manual mode, the FilterBar above
 * it (search and one multiple filter per facet, each value with its count), the pagination below. A column
 * sorts by its key: the list names the sorts it knows, the others are `sortable: false`.
 */
export function ListTable<T>({
  label, queryKey, fetch, columns, facets = [], rowKey, defaultSort, search: searchPlaceholder, onRowClick, rowTone, rowToneLabel,
  empty, activeRow, height = FULL_HEIGHT, refetchInterval = 30_000, views, toolbarExtra, toolbarActions, enabled = true, fixed,
}: {
  label: string;
  queryKey: unknown[];
  fetch: (query: Record<string, unknown>) => Promise<ListResult<T>>;
  /** The columns, or the columns the page's rows call for (a « Brique » column only when one row has a brick). */
  columns: DataTableColumn<T>[] | ((rows: T[]) => DataTableColumn<T>[]);
  facets?: ListFacet[];
  rowKey: string | ((row: T) => string);
  defaultSort: { key: string; dir: 'asc' | 'desc' };
  search: string;
  onRowClick?: (row: T) => void;
  rowTone?: (row: T) => 'neg' | 'warn' | undefined;
  rowToneLabel?: (row: T) => string;
  /** The EmptyState of a list with nothing at all (a search or a filter with no row says so itself). */
  empty: ReactNode;
  /** The row to mark (RG-005-4: the application asked for), among the rows of the page. */
  activeRow?: (row: T) => boolean;
  height?: number;
  refetchInterval?: number | false;
  /** A list with views (one per state…): a Segmented above the table, each view with its count (`views` of the answer). */
  views?: { label: string; items: { id: string; label: string }[]; initial?: string };
  /** A control of the list's own beside the filters (the period of « Disponibilité »). */
  toolbarExtra?: ReactNode;
  /** The toolbar's actions (« Exporter »). */
  toolbarActions?: ReactNode;
  /** False until the list can be asked (a custom period not picked yet): no row, no request. */
  enabled?: boolean;
  /** Filters the list always carries (a deployment's own block, D47): their facet is not offered. */
  fixed?: Record<string, string>;
}) {
  const [view, setView] = useState(views?.initial ?? views?.items[0]?.id);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState(`${defaultSort.key}:${defaultSort.dir}`);
  const chosen = Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(',')]));
  const filter = { ...chosen, ...fixed };

  const list = useQuery({
    queryKey: [...queryKey, view, search, filter, page, pageSize, sort],
    queryFn: () => fetch({ ...(view ? { view } : {}), q: search || undefined, page, pageSize, sort, filter }),
    placeholderData: keepPreviousData,
    refetchInterval,
    enabled,
  });

  const labelOf = (facet: ListFacet, value: string) => facet.valueLabel?.(value) ?? value;
  const filterDefs = facets.filter(f => !fixed || !(f.key in fixed)).map(f => ({
    key: f.key,
    label: f.label,
    type: 'multi' as const,
    options: Object.entries(list.data?.facets[f.key] ?? {})
      .sort(([a], [b]) => labelOf(f, a).localeCompare(labelOf(f, b), 'fr'))
      .map(([value, count]) => ({ value, label: `${labelOf(f, value)} (${count})` })),
  }));
  const total = list.data?.total ?? 0;
  const narrowed = Boolean(search) || Object.keys(chosen).length > 0;
  const rows = enabled ? list.data?.items ?? [] : [];

  const table = (
    <DataTable
      bordered={false}
      height={height}
      label={label}
      columns={typeof columns === 'function' ? columns(rows) : columns}
      rows={rows}
      rowKey={rowKey}
      manual
      totalCount={total}
      toolbarStart={
        <Inline gap={8} align="center">
        {toolbarExtra}
        <FilterBar
          label={t('list.filters', { list: label })}
          filters={filterDefs}
          value={filters}
          onChange={v => { setFilters(v as Record<string, string[]>); setPage(1); }}
          search={search}
          onSearchChange={q => { setSearch(q); setPage(1); }}
          searchPlaceholder={searchPlaceholder}
        />
        </Inline>
      }
      {...(toolbarActions ? { toolbarActions } : {})}
      toolbarSearch={false}
      toolbarFilters={false}
      toolbarDensity={false}
      density="compact"
      loading={enabled && list.isPending}
      defaultSort={defaultSort}
      onQueryChange={q => { if (q.sort) { setSort(`${q.sort.key}:${q.sort.dir}`); setPage(1); } }}
      activeRowKey={activeRow ? (() => { const row = list.data?.items.find(activeRow); return row ? (typeof rowKey === 'function' ? rowKey(row) : String((row as Record<string, unknown>)[rowKey])) : undefined; })() : undefined}
      {...(onRowClick ? { onRowClick } : {})}
      {...(rowTone ? { rowTone, ...(rowToneLabel ? { rowToneLabel } : {}) } : {})}
      empty={narrowed ? <EmptyState framed={false} icon="filter_alt_off" title={t('inventory.noResult')} /> : empty}
      pagination={{ page, pageSize, total, onPageChange: setPage, pageSizes: [25, 50, 100], onPageSizeChange: size => { setPageSize(size); setPage(1); } }}
    />
  );
  if (!views) return table;
  return (
    <Stack gap={16}>
      <Segmented label={views.label} size="sm" value={view ?? ''} onChange={v => { setView(v); setPage(1); }}
        items={views.items.map(v => ({ id: v.id, label: v.label, count: list.data?.views[v.id] }))} />
      {table}
    </Stack>
  );
}
